import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Box,
  useTheme,
  useMediaQuery,
  Container,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { brand } from '../brandTheme';

const Navbar = ({ activeRoute = 'home' }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const linkIsActive = (href) => {
    const segment = href.replace(/^#\//, '') || 'home';
    return activeRoute === segment;
  };

  const menuItems = [
    { label: 'Home', href: '#/home' },
    { label: 'About', href: '#/about' },
    { label: 'Services', href: '#/services' },
    { label: 'Insurance', href: '#/insurance' },
    { label: 'Emi Calculator', href: '#/emi-calculator' },
    { label: 'Contact', href: '#/contact' },
  ];

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const handleScrollTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (mobileOpen) setMobileOpen(false);
  };

  const linkColor = 'rgba(255,255,255,0.95)';
  const logoMain = '#fff';
  const logoAccent = '#99f6e4';

  const drawer = (
    <Box
      sx={{
        textAlign: 'center',
        height: '100%',
        background: '#FFFFFF',
        color: brand.slate,
        display: 'flex',
        flexDirection: 'column',
        pt: 2,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2 }}>
        <IconButton onClick={handleDrawerToggle} sx={{ color: brand.slate }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Typography variant="h6" sx={{ my: 3, color: brand.forest, fontWeight: 800, letterSpacing: 0.5 }}>
        Om Sai <span style={{ color: brand.primary }}>Enterprises</span>
      </Typography>
      <List sx={{ px: 2 }}>
        {menuItems.map((item) => (
          <ListItem
            button
            component="a"
            href={item.href}
            key={item.label}
            onClick={(e) => {
              e.stopPropagation();
              handleScrollTop();
            }}
            sx={{
              textAlign: 'center',
              borderRadius: 2,
              mb: 1,
              '&:hover': { background: brand.surfaceWarm, color: brand.primary },
            }}
          >
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{ fontWeight: 600, color: 'inherit' }}
            />
          </ListItem>
        ))}
        <ListItem sx={{ justifyContent: 'center', mt: 3 }}>
          <Button
            variant="contained"
            color="primary"
            href="#/contact"
            onClick={handleScrollTop}
            sx={{
              width: '100%',
              borderRadius: '999px',
              py: 1.5,
              fontWeight: 700,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none', backgroundColor: brand.primaryDark },
            }}
          >
            Get Started
          </Button>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        backgroundColor: 'rgba(15, 23, 42, 0.36)',
        backdropFilter: 'blur(16px) saturate(140%)',
        WebkitBackdropFilter: 'blur(16px) saturate(140%)',
        color: linkColor,
        borderBottom: '1px solid rgba(255,255,255,0.12)',
        transition: 'background-color 0.3s ease, border-color 0.3s ease, backdrop-filter 0.3s ease',
        py: 0.5,
      }}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ minHeight: { xs: 64, md: 72 }, justifyContent: 'space-between' }}>
          <Typography
            variant="h6"
            component="a"
            href="#/home"
            onClick={handleScrollTop}
            sx={{
              fontWeight: 800,
              color: logoMain,
              textDecoration: 'none',
              cursor: 'pointer',
              letterSpacing: 0.3,
              fontSize: { xs: '1.05rem', md: '1.2rem' },
            }}
          >
            Om Sai{' '}
            <Box component="span" sx={{ color: logoAccent }}>
              Enterprises
            </Box>
          </Typography>

          {isMobile ? (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="end"
              onClick={handleDrawerToggle}
              sx={{ color: linkColor }}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, justifyContent: 'center', mx: 4 }}>
              {menuItems.map((item) => {
                const active = linkIsActive(item.href);
                return (
                  <Typography
                    key={item.label}
                    component="a"
                    href={item.href}
                    onClick={handleScrollTop}
                    sx={{
                      fontWeight: active ? 700 : 500,
                      fontSize: '0.9375rem',
                      color: linkColor,
                      textDecoration: 'none',
                      px: 1.25,
                      py: 0.5,
                      whiteSpace: 'nowrap',
                      borderBottom: active ? '2px solid rgba(153,246,228,0.95)' : '2px solid transparent',
                      '&:hover': { opacity: 0.88 },
                    }}
                  >
                    {item.label}
                  </Typography>
                );
              })}
            </Box>
          )}

          {!isMobile && (
            <Button
              variant="contained"
              href="#/contact"
              onClick={handleScrollTop}
              sx={{
                borderRadius: '999px',
                px: 3.5,
                py: 1,
                fontWeight: 700,
                textTransform: 'none',
                fontSize: '0.875rem',
                boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
                bgcolor: brand.heroCtaPill,
                color: brand.heroCtaPillText,
                '&:hover': {
                  bgcolor: '#e6fffa',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.22)',
                },
              }}
            >
              Get Started
            </Button>
          )}
        </Toolbar>
      </Container>
      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 300 },
        }}
      >
        {drawer}
      </Drawer>
    </AppBar>
  );
};

export default Navbar;
